import React, { Component, RefObject } from 'react'
import { RangeType } from 'shape-edit/types'
import ShapeType from 'shape-entities/types/shape';
import { getPangoLineRanges, getPangoTextIndex, hasPangoMarkup, stripPangoMarkup } from 'svg-text-utils/pango-markup';

const {min, max, round} = Math
interface Props {
    h: number
    mainText: RefObject<SVGTextElement>
    tspanList: RefObject<SVGTSpanElement>[]
    textContent: string
    cursorPosition: number
    textSelection: RangeType
    textRanges: RangeType[]
    setCursorPosition: Function
    shape: ShapeType
    updateShape: Function
    scalarToPosition: Function
}

interface SelectionRect {
    key: string
    x: number
    y: number
    width: number
}

interface State {
    isUpdating: boolean
}

class TextCursorSVG extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            isUpdating: false,
        }
    }

    rerender = () => {
        this.setState({ isUpdating: true })
        setTimeout(() => {
            this.setState({ isUpdating: false })    //wait until all texts are rendered into <text><tspan>...</tspan></text>
        }, 200)
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps && prevProps.textContent !== this.props.textContent) {
            this.rerender()
        }
    }

    /**
     * absolute x/width of the [from, to) character range within a single tspan/text element
     */
    getCharRect = (el: SVGTextElement | SVGTSpanElement, from: number, to: number): { x: number, y: number, width: number } | null => {
        if (!el || !el.textContent || !el.textContent.length) {
            return null
        }

        const len = el.textContent.length
        const startIdx = max(0, min(from, len - 1))
        const endIdx = max(0, min(to - 1, len - 1))

        if (endIdx < startIdx) {
            return null
        }

        try {
            const startPos = el.getStartPositionOfChar(startIdx)
            const endPos = el.getEndPositionOfChar(endIdx)
            return { x: startPos.x, y: startPos.y, width: endPos.x - startPos.x }
        } catch (error) {
            console.error('error getting selection char rect', error, { from, to })
            return null
        }
    }

    getLineElements = (): (SVGTextElement | SVGTSpanElement)[] => {
        const { tspanList, mainText } = this.props

        return tspanList && tspanList.length ? tspanList.map(ref => ref.current) : [mainText && mainText.current]
    }

    /**
     * pango markup: the cursor/selection positions are in the text_content with the tags, but the rendered lines contain
     * the plain text only - each line's position in the plain text (whitespace between the words may be collapsed)
     */
    getMarkupLineRanges = (plainText: string): RangeType[] => {
        return getPangoLineRanges(plainText, this.getLineElements().map(el => el && el.textContent ? el.textContent : ''))
    }

    getMarkupPosition = (plainPos: number, lineRanges: RangeType[]): { x: number, y: number } => {
        let y = 0
        lineRanges.forEach((range, j) => {
            if (plainPos >= range.from) {
                y = j
            }
        })

        return { x: max(0, plainPos - (lineRanges[y] ? lineRanges[y].from : 0)), y }
    }

    getMarkupSelectionRects = (plainSelection: RangeType, lineRanges: RangeType[]): SelectionRect[] => {
        const elements = this.getLineElements()
        const rects: SelectionRect[] = []

        lineRanges.forEach((range, y) => {
            const selFrom = max(plainSelection.from, range.from)
            const selTo = min(plainSelection.to, range.to)
            const rect = selTo > selFrom && elements[y] ? this.getCharRect(elements[y], selFrom - range.from, selTo - range.from) : null
            if (rect) {
                rects.push({ key: `sel-${y}`, ...rect })
            }
        })

        return rects
    }

    getSelectionRects = (): SelectionRect[] => {
        const { textSelection, tspanList, mainText, textRanges } = this.props

        if (!textSelection || textSelection.to <= textSelection.from) {
            return []
        }

        const rects: SelectionRect[] = []

        if (tspanList && tspanList.length) {
            (textRanges || []).forEach((range, y) => {
                const lineFrom = range.from
                const lineTo = typeof range.to === 'number' ? range.to : Infinity

                const selFrom = max(textSelection.from, lineFrom)
                const selTo = min(textSelection.to, lineTo + 1)

                if (selTo > selFrom) {
                    const el = tspanList[y] && tspanList[y].current
                    const rect = el ? this.getCharRect(el, selFrom - lineFrom, selTo - lineFrom) : null
                    if (rect) {
                        rects.push({ key: `sel-${y}`, ...rect })
                    }
                }
            })
        } else if (mainText && mainText.current) {
            const rect = this.getCharRect(mainText.current, textSelection.from, textSelection.to)
            if (rect) {
                rects.push({ key: 'sel-0', ...rect })
            }
        }

        return rects
    }

    render() {
        const { tspanList, mainText, cursorPosition, textSelection, shape, h } = this.props
        const markup = shape && shape.text_content && hasPangoMarkup(shape.text_content) ? shape.text_content : null

        let selectionRects: SelectionRect[]
        let pos: { x: number, y: number }

        if (markup) {
            const lineRanges = this.getMarkupLineRanges(stripPangoMarkup(markup))
            const plainSelection = textSelection && textSelection.to > textSelection.from
                ? { from: getPangoTextIndex(markup, textSelection.from), to: getPangoTextIndex(markup, textSelection.to) }
                : null
            selectionRects = plainSelection ? this.getMarkupSelectionRects(plainSelection, lineRanges) : []
            pos = this.getMarkupPosition(getPangoTextIndex(markup, cursorPosition), lineRanges)
        } else {
            selectionRects = this.getSelectionRects()
            pos = this.props.scalarToPosition(cursorPosition, false)
        }

        let cursorAbsX = 0
        let cursorAbsY = 0

        const el = tspanList && typeof tspanList[pos.y] !== 'undefined' ? tspanList[pos.y].current : (mainText && mainText.current ? mainText.current : null)
        if (el && pos.x >= 0) {
            const isTextLength = el.textContent && el.textContent.length > 0
            const afterLastCharPosition = isTextLength ? el.textContent.length - 1 : 0

            if (isTextLength) {
                try {
                    const r = pos.x <= afterLastCharPosition ? el.getStartPositionOfChar(pos.x) : el.getEndPositionOfChar(afterLastCharPosition)
                    cursorAbsX = r.x
                    cursorAbsY = r.y //+ offsetY
                } catch (error) {
                    console.error('error', error, {afterLastCharPosition, pos})
                }
            }
        }

        return (
            <g style={{ pointerEvents: 'none' }}>
                {selectionRects.map((rect) => (
                    <rect
                        key={rect.key}
                        x={rect.x}
                        y={-.6 * h + rect.y}
                        width={rect.width}
                        height={1.2 * h}
                        fill="#3390ff"
                        fillOpacity={0.35}
                    />
                ))}
                <line
                    x1={cursorAbsX}
                    x2={cursorAbsX}
                    y1={-.6 * h + cursorAbsY}
                    y2={.6 * h + cursorAbsY}
                />
                <animate
                    attributeType="XML"
                    attributeName="stroke"
                    values="#3a3a3a;#ffffff;#cccccc;#3a3a3a"
                    dur="1.5s"
                    repeatCount="indefinite"
                />
            </g>
        )
    }
}

export default TextCursorSVG
